import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Package, X } from "lucide-react";

/**
 * PRODUCT SEARCH DIALOG — barkodu olmayan ürünleri (manav tartı, açık ürün)
 * stok sayımı listesine hızlı eklemek için.
 * - Debounce'lu arama: kod, barkod, ad içinde
 * - İlk 20 sonuç, dokunmatik dostu büyük satırlar
 * - Satıra tıklandığında parent'a seçimi geri verir → lookupAndAdd gibi eklenir
 */
export default function ProductSearchDialog({ open, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const tRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setResults([]);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (tRef.current) clearTimeout(tRef.current);
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    tRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/products?search=${encodeURIComponent(term)}&limit=20&include_inactive=true&include_zero_price=true`);
        setResults(Array.isArray(r.data) ? r.data.slice(0, 20) : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(tRef.current);
  }, [q, open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#FAF7F2] border-[#E8E2D5] max-w-xl max-h-[92vh] flex flex-col p-0" data-testid="product-search-dialog">
        <DialogHeader className="p-4 pb-2 shrink-0">
          <DialogTitle className="font-heading flex items-center gap-2">
            <Search className="w-5 h-5 text-[#2E5A36]" /> Barkodsuz Ürün Ara
          </DialogTitle>
          <DialogDescription className="text-xs text-[#1B2A22]/70">
            Manav tartı, açık ürün gibi barkodu olmayanları ad veya kod ile ara, listeye ekle.
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 pb-3 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1B2A22]/40" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ad, STOKKODU veya barkod yaz (min 2 karakter)..."
              className="pl-10 h-12 text-base bg-white"
              data-testid="product-search-input"
            />
          </div>
          <p className="text-[11px] text-[#1B2A22]/50 mt-1">
            {loading ? "aranıyor..." : q.trim().length < 2 ? "en az 2 karakter yaz" : `${results.length} sonuç`}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {results.length === 0 && q.trim().length >= 2 && !loading && (
            <div className="text-center py-10 text-sm text-[#1B2A22]/50">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
              Eşleşen ürün yok
            </div>
          )}
          <div className="space-y-1.5">
            {results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onPick(p); onClose(); }}
                className="w-full text-left bg-white hover:bg-[#2E5A36]/5 border border-[#E8E2D5] hover:border-[#2E5A36] rounded-xl p-3 transition"
                data-testid={`product-search-result-${p.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#1B2A22]/5 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-5 h-5 text-[#1B2A22]/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-sm text-[#1B2A22] truncate">
                      {p.name_tr}
                      {(p.sell_by === "kg" || (p.unit || "").toLowerCase() === "kg") && (
                        <span className="ml-2 text-[10px] bg-[#2E5A36] text-white rounded-full px-1.5 py-0.5 font-heading font-bold align-middle">KG</span>
                      )}
                    </p>
                    <p className="text-[11px] font-mono text-[#1B2A22]/60 truncate">
                      {p.akinsoft_code || "—"} · {p.barcode || "barkodsuz"}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono font-bold text-sm text-[#D9653B]">
                      ₺{(p.price || 0).toFixed(2)}
                      {(p.sell_by === "kg" || (p.unit || "").toLowerCase() === "kg") && (
                        <span className="text-[10px] text-[#1B2A22]/60">/kg</span>
                      )}
                    </p>
                    <p className="text-[10px] font-mono text-[#1B2A22]/60">
                      stok: {p.stock ?? 0} {(p.sell_by === "kg" || (p.unit || "").toLowerCase() === "kg") ? "kg" : "ad"}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
